import React from 'react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from '@emotion/styled';
import { B300, B400, N30, N80, N200, N500, N900 } from '@atlaskit/theme/colors';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import debounce from 'lodash/debounce';

import {
  FooterLogo,
  SocialSignUpDivider,
  TextInput,
  SocialSignUpButton
} from '../components';
import { messages } from '../../messages';
import {
  SanitizedFormattedHtmlMessage,
  useStargateClient
} from '@atlassiansox/bxpkit-core';
import { useSignUpFormGlobalConfiguration } from '../../../../hook';

const GOOGLE_ICON_URL =
  'https://aid-frontend.prod.atl-paas.net/atlassian-id/front-end/5.0.51/static/media/google-logo.c21ca9d1.svg';

export function FirstImpressionsLoggedOutView() {
  const [useEmail, setUseEmail] = useState(false);
  const [formSubmitError, setFormSubmitError] = useState(false);
  const stargateClient = useStargateClient();
  const { checkEmailPageUrl } = useSignUpFormGlobalConfiguration();

  const validateSsoDebounced = debounce(async (email: string) => {
    console.log('validating sso');
    try {
      const {
        samlRequired,
        methods: [{ redirect_to = '' }]
      } = await stargateClient.checkAuthenticationMethodsAvailable({
        email: email,
        successRedirect: window.location.href,
        webPlatform: 'magnolia'
      });

      if (samlRequired) {
        return messages.firstImpressionsSamlRequired.defaultMessage?.replace(
          '{loginUrl}',
          redirect_to
        );
      }
      return undefined;
    } catch (error) {
      console.log(error);
      return messages.firstImpressionsSsoCheckError.defaultMessage as string;
    }
  }, 500);

  const SignupWithEmailButton = () => {
    return (
      <DefaultButton
        data-testid="signup-with-email-button"
        onClick={() => setUseEmail(true)}
      >
        <FormattedMessage {...messages.socialSignUpButtonSignUpWithEmail} />
      </DefaultButton>
    );
  };

  const SignupWithEmailForm = () => (
    <Formik
      initialValues={{
        email: '',
        fullName: ''
      }}
      validationSchema={Yup.object({
        fullName: Yup.string()
          .max(255, () => (
            <FormattedMessage {...messages.inputLabelFullNameLengthError} />
          ))
          .required(() => (
            <FormattedMessage {...messages.inputLabelRequired} />
          )),
        email: Yup.string()
          .email(() => (
            <FormattedMessage {...messages.inputLabelWorkEmailError} />
          ))
          .required(() => <FormattedMessage {...messages.inputLabelRequired} />)
      })}
      onSubmit={async (values) => {
        setFormSubmitError(false);

        try {
          await stargateClient.userSignup({
            displayName: values.fullName,
            email: values.email,
            redirectTo: window.location.href,
            locale: 'en',
            webPlatform: 'magnolia'
          });
          window.location.assign(checkEmailPageUrl);
        } catch (error) {
          setFormSubmitError(true);
        }
      }}
    >
      {({ dirty, isSubmitting, isValid }) => (
        <Form>
          <TextInput
            validateFn={validateSsoDebounced}
            autoComplete="email"
            testId="first-impressions-work-email-text-input"
            name="email"
            type="email"
            label={<FormattedMessage {...messages.inputLabelWorkEmail} />}
          />

          <TextInput
            testId="first-impressions-full-name-text-input"
            name="fullName"
            type="text"
            label={<FormattedMessage {...messages.inputLabelFullName} />}
          />

          <Eula>
            <SanitizedFormattedHtmlMessage {...messages.firstImpressionsEula} />
          </Eula>

          <SubmitButton
            type="submit"
            disabled={!dirty || isSubmitting || !isValid}
          >
            <FormattedMessage {...messages.submitButtonLabel} />
          </SubmitButton>
          {formSubmitError && (
            <FailedSubmitMessage>
              <FormattedMessage
                {...messages.firstImpressionsLoggedOutFormSubmitError}
              />
            </FailedSubmitMessage>
          )}
        </Form>
      )}
    </Formik>
  );

  const SignupWithEmailFormFooter = () => (
    <>
      <NoCreditCardRequiredFooterMessage>
        <FormattedMessage {...messages.noCreditCardRequiredFooter} />
      </NoCreditCardRequiredFooterMessage>
      <ReCaptchaEula>
        <SanitizedFormattedHtmlMessage
          {...messages.firstImpressionsRecaptcha}
        />
      </ReCaptchaEula>
    </>
  );

  return (
    <FirstImpressionsLoggedOutViewContainer>
      <FormHeaderContainer>
        <FormHeaderHeading>
          <FormattedMessage {...messages.firstImpressionsLoggedOutFormHeader} />
        </FormHeaderHeading>
        <FormHeaderSubHeading>
          <FormattedMessage
            {...messages.firstImpressionsLoggedOutFormSubHeader}
          />
        </FormHeaderSubHeading>
      </FormHeaderContainer>

      <SocialSignUpButton
        href={''}
        onClick={() => undefined}
        label={
          <FormattedMessage
            {...messages.socialSignUpButtonContinueWithGoogle}
          />
        }
        iconUrl={GOOGLE_ICON_URL}
      />
      <SocialSignUpDivider />

      {useEmail ? (
        <>
          <SignupWithEmailForm />
          <SignupWithEmailFormFooter />
        </>
      ) : (
        <SignupWithEmailButton />
      )}

      <FooterLogo />
    </FirstImpressionsLoggedOutViewContainer>
  );
}

const fontCharlieText =
  '"Charlie Text", -apple-system, BlinkMacSystemFont, "Segoe UI",' +
  'Roboto, "Noto Sans", Ubuntu, "Droid Sans", "Helvetica Neue", sans-serif !important';

const FirstImpressionsLoggedOutViewContainer = styled.div`
  width: 100%;
  margin: 0;
  display: flex;
  flex-direction: column;
`;

const FormHeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
`;

const FormHeaderHeading = styled.div`
  color: ${N900};
  font-family: 'Charlie Display';
  font-size: 36px;
  font-weight: 500;
  line-height: 40px;
`;

const FormHeaderSubHeading = styled.div`
  color: ${N900};
  font-family: 'Charlie Display';
  font-size: 16px;
  font-weight: 400;
  line-height: 19px;
  margin: 0 0 16px 0;
`;

const NoCreditCardRequiredFooterMessage = styled.p`
  font-family: Charlie Display;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 17.5px;
  letter-spacing: 1px;
  align-items: center;
  text-align: center;
  text-transform: uppercase;
  margin: 16px 0 40px 0;
`;

const SubmitButton = styled.button`
  font-family: ${fontCharlieText};
  font-weight: 500;
  font-size: 16px;
  outline: none !important;
  border: none;
  border-radius: 3px;
  color: white;
  background-color: ${B400};
  width: 100%;
  height: 36px;
  &:disabled {
    color: ${N80};
    background-color: ${N30};
    opacity: 0.6;
  }
`;

const Eula = styled.div`
  font-family: ${fontCharlieText};
  font-size: 12px;
  line-height: 16px;
  color: ${N200};
  vertical-align: middle;
  padding: 0;
  font-weight: 400;

  a {
    color: ${B400};
    cursor: pointer;
    text-decoration: none;
    &:hover {
      color: ${B300};
      text-decoration: underline;
    }
  }

  p {
    font-size: 12px;
    line-height: 16px;
    margin-bottom: 24px;
  }
`;

const ReCaptchaEula = styled.div`
  font-size: 12px;
  color: ${N200};
  line-height: 16px;
  text-align: center;
  font-family: ${fontCharlieText};
  margin-bottom: 16px;
  > * {
      margin-block-start: 0;
      > a {     
        color: ${N200};
        text-decoration: underline;        
      }
    }
}
`;

const DefaultButton = styled.div`
  height: 39px;
  width: 100%;
  border-radius: 3px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${N500};
  cursor: pointer;
  font-weight: 500;
  box-shadow: rgba(0, 0, 0, 0.2) 1px 1px 5px 0 !important;

  margin: 0px 0px 20px 0px;
  padding: 0;
  border: 0;
  font-size: 100%;
  vertical-align: baseline;
  font-family: ${fontCharlieText};
  font-style: normal;
  background-color: #e8e9ee;
`;

const FailedSubmitMessage = styled.div`
  color: #de350b;
  font-weight: 400;
  font-size: 12px;
  font-family: ${fontCharlieText};
`;
